import React from "react";
import { _t } from "src/utils";
import "./alfred-ineligible-blocker.scss";

interface Props {
  /**
   * One of the codes returned by `GET /psp/alfred/eligibility`:
   *   - ALFRED_NOT_AVAILABLE_FOR_COUNTRY
   *   - ALFRED_CUSTOMER_NOT_PROVISIONED
   *   - ALFRED_KYC_PENDING
   *   - ALFRED_KYC_REJECTED
   *   - USER_NOT_VERIFIED
   * Unknown codes fall back to a generic "not available" message.
   */
  code: string;
}

interface BlockerCopy {
  title: string;
  message: string;
}

const resolveCopy = (rawCode: string): BlockerCopy => {
  // Eligibility returns the enum code with a trailing request id, e.g.
  // "ALFRED_NOT_AVAILABLE_FOR_COUNTRY (DF18CBC3)" — match the leading token.
  const code = rawCode.match(/^[A-Z0-9_]+/)?.[0] ?? rawCode;
  switch (code) {
    case "ALFRED_NOT_AVAILABLE_FOR_COUNTRY":
      return {
        title: _t("ALFRED.BLOCKER.COUNTRY_TITLE", {
          defaultValue: "Not available in your region",
        }),
        message: _t("ALFRED.BLOCKER.COUNTRY_MESSAGE", {
          defaultValue: "This payment method is not currently supported in your country.",
        }),
      };
    case "ALFRED_CUSTOMER_NOT_PROVISIONED":
      return {
        title: _t("ALFRED.BLOCKER.PROVISIONING_TITLE", {
          defaultValue: "Account setup in progress",
        }),
        message: _t("ALFRED.BLOCKER.PROVISIONING_MESSAGE", {
          defaultValue:
            "Your payment account is being prepared. Please try again in a few minutes — if it persists, contact support.",
        }),
      };
    case "ALFRED_KYC_PENDING":
      return {
        title: _t("ALFRED.BLOCKER.KYC_PENDING_TITLE", {
          defaultValue: "Verification in review",
        }),
        message: _t("ALFRED.BLOCKER.KYC_PENDING_MESSAGE", {
          defaultValue: "Your account verification is still under review. Please try again shortly.",
        }),
      };
    case "ALFRED_KYC_REJECTED":
      return {
        title: _t("ALFRED.BLOCKER.KYC_REJECTED_TITLE", {
          defaultValue: "Verification rejected",
        }),
        message: _t("ALFRED.BLOCKER.KYC_REJECTED_MESSAGE", {
          defaultValue: "Your account verification was rejected by the payment provider. Please contact support.",
        }),
      };
    case "USER_NOT_VERIFIED":
      return {
        title: _t("ALFRED.BLOCKER.UNVERIFIED_TITLE", {
          defaultValue: "Verify your account",
        }),
        message: _t("ALFRED.BLOCKER.UNVERIFIED_MESSAGE", {
          defaultValue: "Please verify your account to continue with this payment method.",
        }),
      };
    default:
      return {
        title: _t("ALFRED.BLOCKER.GENERIC_TITLE", {
          defaultValue: "Not available",
        }),
        message: _t("ALFRED.BLOCKER.GENERIC_MESSAGE", {
          defaultValue: "This payment method is not available for your account right now.",
        }),
      };
  }
};

const AlfredIneligibleBlocker: React.FC<Props> = ({ code }) => {
  const { title, message } = resolveCopy(code);
  // Title/message already convey the error — show only the parenthesized
  // request id (e.g. "(931D41F6)") as a support reference, not the enum.
  const reference = code.match(/\([^)]+\)/)?.[0];
  // A hard rejection is terminal (contact support) — show it as an error
  // with a red ✕, distinct from the amber "!" used for transient states
  // (in review / provisioning / verify-your-account).
  const isError = /^ALFRED_KYC_REJECTED/.test(code);
  return (
    <div className="alfred-blocker">
      <div
        className={`alfred-blocker__icon${
          isError ? " alfred-blocker__icon--error" : ""
        }`}
      >
        {isError ? "✕" : "!"}
      </div>
      <h2 className="alfred-blocker__title">{title}</h2>
      <p className="alfred-blocker__message">{message}</p>
      {reference && <p className="alfred-blocker__code">{reference}</p>}
    </div>
  );
};

export default AlfredIneligibleBlocker;
